/**
 * AdminScreen — Protected admin dashboard for beta feedback review.
 *
 * Only reachable when isAdmin === true. Shows per-wave stats and
 * a chronological feedback inbox with attachment links.
 *
 * UI-INV-005: Leaf component, props only.
 */
import { useEffect, useState } from "react";
import { fetchAdminFeedback, type WaveStat, type FeedbackRow } from "../services/feedbackService";
import { MetaStrip } from "../components/MetaStrip";
import styles from "./AdminScreen.module.css";
import { TEST_IDS } from "../testing/testIds";

type Props = {
  onBack: () => void;
};

export function AdminScreen({ onBack }: Props) {
  const [waveStats, setWaveStats] = useState<WaveStat[]>([]);
  const [feedback, setFeedback] = useState<FeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      const result = await fetchAdminFeedback();

      if (cancelled) return;

      if (result.error) {
        setError(result.error);
        setLoading(false);
        return;
      }

      setWaveStats(result.waveStats);
      setFeedback(result.feedback);
      setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className={styles.screen} data-testid={TEST_IDS.ADMIN_SCREEN}>
      <MetaStrip label="Admin" onBack={onBack} />

      <div className={styles.content}>
        {loading && <p className={styles.state}>Loading…</p>}

        {error && <p className={styles.stateError}>{error}</p>}

        {!loading && !error && (
          <>
            {/* Wave summary */}
            {waveStats.length > 0 && (
              <section className={styles.section}>
                <div className={styles.sectionLabel}>Wave summary</div>
                <table
                  className={styles.table}
                  data-testid={TEST_IDS.ADMIN_WAVE_TABLE}
                >
                  <thead>
                    <tr>
                      <th>Wave</th>
                      <th>Submissions</th>
                      <th>Avg ease</th>
                      <th>Avg trust</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waveStats.map((s) => (
                      <tr key={s.wave}>
                        <td>{s.wave}</td>
                        <td>{s.submissions}</td>
                        <td>{s.avgEase.toFixed(1)}</td>
                        <td>{s.avgTrust.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {/* Feedback inbox */}
            <section className={styles.section}>
              <div className={styles.sectionLabel}>
                Feedback inbox ({feedback.length})
              </div>

              {feedback.length === 0 && (
                <p className={styles.state}>No submissions yet.</p>
              )}

              <div
                className={styles.cards}
                data-testid={TEST_IDS.ADMIN_FEEDBACK_LIST}
              >
                {feedback.map((f) => (
                  <div
                    key={f.id}
                    className={styles.card}
                    data-testid={TEST_IDS.ADMIN_FEEDBACK_CARD}
                  >
                    <div className={styles.cardMeta}>
                      <span className={styles.waveBadge}>{f.wave}</span>
                      <span className={styles.cardDate}>
                        {new Date(f.created_at).toLocaleString()}
                      </span>
                      <span className={styles.scores}>
                        Ease {f.ease_of_use_score}/10 · Trust {f.trust_score}/10
                      </span>
                    </div>

                    <p className={styles.confusionLabel}>Confusion</p>
                    <p className={styles.confusionText}>{f.confusion_text}</p>

                    {f.positive_feedback && (
                      <>
                        <p className={styles.confusionLabel}>What worked</p>
                        <p className={styles.confusionText}>{f.positive_feedback}</p>
                      </>
                    )}

                    <div className={styles.attachments}>
                      {f.audio_url && (
                        <span className={styles.attachBadge}>🎙 audio</span>
                      )}
                      {f.screenshot_urls?.length > 0 && (
                        <span className={styles.attachBadge}>
                          🖼 {f.screenshot_urls.length} screenshot{f.screenshot_urls.length !== 1 ? "s" : ""}
                        </span>
                      )}
                      {f.log_bundle_url && (
                        <span className={styles.attachBadge}>📄 log</span>
                      )}
                      {f.contact_permission && (
                        <span className={styles.attachBadge}>✉ may contact</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
